import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import axios from "axios";
import Logo from "../../Images/SkillsMD logo.svg";
import { API_URL } from "../../config/api";
import ToastMessage from "../../config/ToastMessage";

// ─── Reusable Components ──────────────────────────────────────────────────────

const InputField = ({ label, required, placeholder, type = "text", name, value, onChange, error, readOnly }) => (
  <div className="flex flex-col gap-1.5">
    <label className="flex items-center gap-0.5 text-sm font-bold text-neutral-800">
      {label}
      {required && <span className="text-red-600 font-bold">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      readOnly={readOnly}
      className={`h-9 w-full px-3 py-2 rounded shadow-[0px_0px_4px_0px_rgba(32,33,35,0.07)] border text-sm text-neutral-800 placeholder-zinc-400 focus:outline-none transition-all ${
        readOnly
          ? "bg-gray-100 border-gray-200 cursor-not-allowed text-zinc-500"
          : `bg-white focus:border-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5] ${
              error ? "border-red-400" : "border-gray-200"
            }`
      }`}
    />
    {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
  </div>
);

// ─── Password Field with Eye Toggle ──────────────────────────────────────────
const PasswordField = ({ label, required, placeholder, name, value, onChange, error }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="flex flex-col gap-1.5">
      <label className="flex items-center gap-0.5 text-sm font-bold text-neutral-800">
        {label}
        {required && <span className="text-red-600 font-bold">*</span>}
      </label>
      <div
        className={`relative h-9 w-full bg-white rounded shadow-[0px_0px_4px_0px_rgba(32,33,35,0.07)] border flex items-center transition-all ${
          error
            ? "border-red-400"
            : "border-gray-200 focus-within:border-[#4AACC5] focus-within:ring-1 focus-within:ring-[#4AACC5]"
        }`}
      >
        <input
          type={show ? "text" : "password"}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="h-full w-full px-3 pr-9 bg-transparent text-sm text-neutral-800 placeholder-zinc-400 focus:outline-none"
        />
        <button
          type="button"
          onClick={() => setShow((prev) => !prev)}
          tabIndex={-1}
          className="absolute right-2.5 text-zinc-400 hover:text-[#4AACC5] transition-colors"
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
    </div>
  );
};

const SelectField = ({ label, required, placeholder, name, value, onChange, options = [], disabled = false, error }) => (
  <div className="flex flex-col gap-1.5">
    <label className="flex items-center gap-0.5 text-sm font-bold text-neutral-800">
      {label}
      {required && <span className="text-red-600 font-bold">*</span>}
    </label>
    <div
      className={`relative h-9 rounded shadow-[0px_0px_4px_0px_rgba(32,33,35,0.07)] border flex items-center transition-all ${
        disabled
          ? "bg-gray-200 border-gray-200"
          : `bg-white ${error ? "border-red-400" : "border-gray-200"} focus-within:border-[#4AACC5] focus-within:ring-1 focus-within:ring-[#4AACC5]`
      }`}
    >
      <select
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={`w-full h-full px-3 bg-transparent text-sm appearance-none focus:outline-none ${
          value ? "text-neutral-800" : "text-zinc-400"
        } ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <svg className="absolute right-3 w-3 h-3 text-zinc-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </div>
    {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
  </div>
);

// ─── Phone Field ──────────────────────────────────────────────────────────────
const PhoneField = ({ label, required, value, onChange, error }) => (
  <div className="flex flex-col gap-1.5">
    <label className="flex items-center gap-0.5 text-sm font-bold text-neutral-800">
      {label}
      {required && <span className="text-red-600 font-bold">*</span>}
    </label>
    <PhoneInput
      country={"us"}
      value={value}
      onChange={onChange}
      inputStyle={{
        width: "100%",
        height: "36px",
        fontSize: "14px",
        borderColor: error ? "#f87171" : "#e5e7eb",
        borderRadius: "4px",
        boxShadow: "0px 0px 4px 0px rgba(32,33,35,0.07)",
        color: "#1f2937",
      }}
      buttonStyle={{
        borderColor: error ? "#f87171" : "#e5e7eb",
        borderRadius: "4px 0 0 4px",
        backgroundColor: "#fff",
      }}
      containerStyle={{ width: "100%" }}
    />
    {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
  </div>
);

// ─── Upload Box ───────────────────────────────────────────────────────────────
const UploadBox = ({ label, required, name, onChange, file, uploading, error }) => {
  const inputRef = useRef(null);
  return (
    <div className="flex flex-col gap-2">
      {label && (
        <label className="flex items-center gap-0.5 text-sm font-bold text-neutral-800">
          {label}
          {required && <span className="text-red-600 font-bold">*</span>}
        </label>
      )}
      <div
        onClick={() => !uploading && inputRef.current?.click()}
        className={`w-full h-24 rounded-[20px] border flex flex-col items-center justify-center gap-1.5 transition-all group
          ${uploading ? "border-[#4AACC5] bg-teal-50/40 cursor-wait" : "border-stone-300 hover:border-[#4AACC5] hover:bg-teal-50/30 cursor-pointer"}
          ${error ? "!border-red-400" : ""}`}
      >
        <input
          ref={inputRef}
          type="file"
          name={name}
          onChange={onChange}
          className="hidden"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        />
        {uploading ? (
          <>
            <svg className="w-5 h-5 text-[#4AACC5] animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <p className="text-xs text-teal-500">Uploading...</p>
          </>
        ) : file ? (
          <>
            <svg className="w-6 h-6 text-[#4AACC5]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-xs text-[#4AACC5] font-medium text-center px-3 truncate max-w-full">{file.name}</p>
          </>
        ) : (
          <>
            <svg className="w-6 h-6 text-zinc-400 group-hover:text-[#4AACC5] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            <p className="text-xs text-zinc-400 group-hover:text-teal-500 transition-colors">Click to upload</p>
          </>
        )}
      </div>
      {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
    </div>
  );
};

const SectionCard = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-3xl border border-zinc-300 overflow-hidden">
    <div className="flex flex-col items-start bg-gray-50 px-9 py-6 rounded-tl-3xl rounded-tr-3xl">
      <h3 className="text-xl font-medium text-stone-900 leading-9">{title}</h3>
      <p className="text-stone-900/60 text-base font-normal">{subtitle}</p>
    </div>
    <div className="px-9 py-8">{children}</div>
  </div>
);

// ─── Country Options ──────────────────────────────────────────────────────────
const COUNTRIES = [
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "UK", label: "United Kingdom" },
  { value: "AU", label: "Australia" },
];

// ─── Initial State ────────────────────────────────────────────────────────────
const initialForm = {
  organizationName: "",
  registrationNumber: "",
  firstName: "",
  lastName: "",
  emailAddress: "",
  streetAddress: "",
  city: "",
  state: "",
  zipCode: "",
  country: "",
  regCertificate: null,
  additionalDoc: null,
  profilePhoto: null,
  userId: "",
  password: "",
  rePassword: "",
};

// ─── Validation ───────────────────────────────────────────────────────────────
const validate = (form, phoneNumber) => {
  const errors = {};
  if (!form.organizationName.trim())   errors.organizationName = "Organization name is required";
  if (!form.registrationNumber.trim()) errors.registrationNumber = "Registration number is required";
  if (!form.firstName.trim())          errors.firstName = "First name is required";
  if (!form.lastName.trim())           errors.lastName = "Last name is required";
  if (!form.emailAddress.trim())       errors.emailAddress = "Email is required";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.emailAddress))
                                       errors.emailAddress = "Enter a valid email";
  if (!phoneNumber)                    errors.phoneNumber = "Phone number is required";
  if (!form.streetAddress.trim())      errors.streetAddress = "Street address is required";
  if (!form.city)                      errors.city = "City is required";
  if (!form.state)                     errors.state = "State is required";
  if (!form.zipCode.trim())            errors.zipCode = "ZIP code is required";
  if (!form.country)                   errors.country = "Country is required";
  if (!form.regCertificate)            errors.regCertificate = "Registration certificate is required";
  if (!form.userId.trim())             errors.userId = "Email / User ID is required";
  if (!form.password)                  errors.password = "Password is required";
  else if (form.password.length < 8)   errors.password = "Minimum 8 characters required";
  if (!form.rePassword)                errors.rePassword = "Please re-enter your password";
  else if (form.password !== form.rePassword)
                                       errors.rePassword = "Passwords do not match";
  return errors;
};

// ─── Azure File Upload Helper ─────────────────────────────────────────────────
const uploadToAzure = async (file) => {
  if (!file) return null;
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch(`${API_URL}/azure-upload/pdf-upload`, {
    method: "POST",
    body: formData,
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || "File upload failed");
  }
  const data = await response.json();
  return data.url;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const TrainingProviderRegistration = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  // Token validation states
  const [tokenValidating, setTokenValidating] = useState(true);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const hasValidated = useRef(false);

  // Per-file upload loading states
  const [uploadingFiles, setUploadingFiles] = useState({
    regCertificate: false,
    additionalDoc: false,
    profilePhoto: false,
    docFile: false,
  });

  // Azure blob URLs after upload
  const [uploadedUrls, setUploadedUrls] = useState({
    regCertificate: null,
    additionalDoc: null,
    profilePhoto: null,
    docFile: null,
  });

  // Phone numbers
  const [phoneNumber, setPhoneNumber] = useState("");
  const [alternatePhone, setAlternatePhone] = useState("");

  // Location — dynamic from API
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  // Extra document row
  const [showDocRow, setShowDocRow] = useState(false);
  const [docType, setDocType] = useState("");
  const [docFile, setDocFile] = useState(null);
  const docFileInputRef = useRef(null);
  const profileInputRef = useRef(null);
  const [toast, setToast] = useState(null);

  // ── Validate Invite Token on Mount ────────────────────────────────────────
  useEffect(() => {
    if (hasValidated.current) return;
    hasValidated.current = true;

    const token = new URLSearchParams(window.location.search).get("token");

    if (!token) {
      setInviteError("Invalid invitation link.");
      setTokenValidating(false);
      return;
    }

    const validateToken = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/validate_invite_token?token=${token}`
        );
        if (response.data.valid) {
          setInviteValid(true);
          if (response.data.email) {
            setForm((prev) => ({
              ...prev,
              userId: response.data.email,
              emailAddress: response.data.email,
            }));
          }
        } else {
          setInviteError("Invalid invitation link.");
        }
      } catch (error) {
        setInviteError(
          error?.response?.data?.detail || "Invitation link expired or invalid."
        );
      } finally {
        setTokenValidating(false);
      }
    };

    validateToken();
  }, []);

  // ── Fetch States on Mount ─────────────────────────────────────────────────
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await axios.get(`${API_URL}/location/states`);
        const data = res.data.data || [];
        setStates(data.map((s) => ({ value: s.code, label: s.name })));
      } catch (err) {
        console.error("Failed to load states", err);
      }
    };
    fetchStates();
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────────

  // ✅ Auto-sync userId when emailAddress is typed
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "emailAddress") {
        updated.userId = value;
      }
      return updated;
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleStateSelect = async (e) => {
    const stateCode = e.target.value;
    setForm((prev) => ({ ...prev, state: stateCode, city: "" }));
    setCities([]);
    if (errors.state) setErrors((prev) => ({ ...prev, state: "" }));
    if (!stateCode) return;
    try {
      setLoadingCities(true);
      const res = await axios.get(`${API_URL}/location/states/${stateCode}/cities`);
      const data = res.data.data || [];
      setCities(data.map((c) => ({ value: c, label: c })));
    } catch (err) {
      console.error("Failed to load cities", err);
    } finally {
      setLoadingCities(false);
    }
  };

  const handleFile = async (e) => {
    const { name, files } = e.target;
    const file = files[0];
    if (!file) return;

    setForm((prev) => ({ ...prev, [name]: file }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));

    setUploadingFiles((prev) => ({ ...prev, [name]: true }));
    try {
      const url = await uploadToAzure(file);
      setUploadedUrls((prev) => ({ ...prev, [name]: url }));
    } catch (err) {
      console.error(`Failed to upload ${name}:`, err);
      setErrors((prev) => ({ ...prev, [name]: "Upload failed. Please try again." }));
      setForm((prev) => ({ ...prev, [name]: null }));
    } finally {
      setUploadingFiles((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleProfilePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm((prev) => ({ ...prev, profilePhoto: file }));
    setUploadingFiles((prev) => ({ ...prev, profilePhoto: true }));
    try {
      const url = await uploadToAzure(file);
      setUploadedUrls((prev) => ({ ...prev, profilePhoto: url }));
    } catch (err) {
      console.error("Failed to upload profile photo:", err);
    } finally {
      setUploadingFiles((prev) => ({ ...prev, profilePhoto: false }));
    }
  };

  const handleDocFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setDocFile(file);
    setUploadingFiles((prev) => ({ ...prev, docFile: true }));
    try {
      const url = await uploadToAzure(file);
      setUploadedUrls((prev) => ({ ...prev, docFile: url }));
    } catch (err) {
      console.error("Failed to upload extra doc:", err);
    } finally {
      setUploadingFiles((prev) => ({ ...prev, docFile: false }));
    }
  };

  const handleRemoveDocRow = () => {
    setShowDocRow(false);
    setDocType("");
    setDocFile(null);
    setUploadedUrls((prev) => ({ ...prev, docFile: null }));
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const errs = validate(form, phoneNumber);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const anyUploading = Object.values(uploadingFiles).some(Boolean);
    if (anyUploading) {
      setApiError("Please wait for all files to finish uploading.");
      return;
    }

    setSubmitting(true);
    setApiError("");

    try {
      const payload = {
        first_name: form.firstName,
        last_name: form.lastName,
        phone: `+${phoneNumber}`,
        ...(alternatePhone && { alternate_phone: `+${alternatePhone}` }),
        email: form.userId,
        password: form.password,
        ...(uploadedUrls.profilePhoto && { avatar_url: uploadedUrls.profilePhoto }),
        address: form.streetAddress,
        city: form.city,
        state: form.state,
        country: form.country,
        zip_code: form.zipCode,
        organization_name: form.organizationName,
        org_type: "TRAINING_PROVIDER",
        organization_email: form.emailAddress,
        registration_number: form.registrationNumber,
        ...(uploadedUrls.regCertificate && {
          registration_certificate_url: uploadedUrls.regCertificate,
        }),
        ...((uploadedUrls.additionalDoc || uploadedUrls.docFile) && {
          support_doc_url: uploadedUrls.additionalDoc || uploadedUrls.docFile,
        }),
        ...(uploadedUrls.profilePhoto && {
          org_avatar_url: uploadedUrls.profilePhoto,
        }),
        token: new URLSearchParams(window.location.search).get("token"),
      };

      const res = await axios.post(`${API_URL}/user/create_admin`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      const successMsg =
        res?.data?.message ||
        res?.data?.detail ||
        "Training provider registered successfully";

      setToast({ type: "success", message: successMsg });
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      console.error("Submission failed:", err);
      const detail = err?.response?.data?.detail || err?.response?.data?.message;
      const message =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg || JSON.stringify(d)).join(", ")
          : "Submission failed. Please try again.";
      setApiError(message);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
    }
  };

  const isAnyUploading = Object.values(uploadingFiles).some(Boolean);

  // ── TOKEN VALIDATING SCREEN ───────────────────────────────────────────────
  if (tokenValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <svg className="w-8 h-8 animate-spin mx-auto text-[#4AACC5]" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          <p className="mt-3 text-gray-600 text-sm">Validating invitation link...</p>
        </div>
      </div>
    );
  }

  // ── INVALID TOKEN SCREEN ──────────────────────────────────────────────────
  if (!inviteValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-10 rounded-2xl shadow-md text-center max-w-md w-full mx-4">
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-5">
            <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-neutral-800 mb-2">Invitation Link Expired</h2>
          <p className="text-sm text-zinc-500 mb-8 leading-relaxed">
            {inviteError || "This invitation link is invalid or has expired. Please request a new invitation."}
          </p>
          <button
            onClick={() => navigate("/login")}
            className="w-full bg-[#4AACC5] text-white text-sm font-bold py-3 rounded-lg hover:bg-[#96d6e5] transition-all"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // ── MAIN FORM ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">

      {/* ── HEADER ── */}
      <header className="bg-[#4AACC5] text-white">
        <div className="max-w-[1440px] mx-auto px-6 py-[18px] flex justify-between items-center border-b border-white/10">
          <div className="w-36 h-9 flex items-center">
            <img src={Logo} alt="SkillsMD" className="h-9 w-auto" />
          </div>
          <button
            onClick={() => navigate("/login")}
            className="flex items-center gap-1 bg-[#4AACC5] border border-white/30 text-white text-xs font-bold px-5 py-2 rounded hover:bg-[#4AACC5] transition-all"
          >
            ← Login
          </button>
        </div>
        <div className="text-center py-10 px-4">
          <h1 className="text-4xl font-bold leading-9 mb-3">Training Provider Registration</h1>
          <p className="text-white/60 text-base font-normal">For Accredited training organizations</p>
        </div>
      </header>

      {/* ── MAIN ── */}
      <main className="flex-grow max-w-[1340px] mx-auto w-full px-6 py-10 flex flex-col gap-6">

        {toast && (
          <ToastMessage
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}

        {/* ── API Error Banner ── */}
        {apiError && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-6 py-4 flex items-start gap-3">
            <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <p className="text-sm text-red-600">{apiError}</p>
          </div>
        )}

        {/* ── SECTION 1: Organization Details ── */}
        <SectionCard
          title="Organization Details"
          subtitle="Basic information about your training organization"
        >
          <div className="flex flex-col gap-7">
            {/* Row 1 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-7">
              <InputField
                label="Organization Name" required
                name="organizationName" value={form.organizationName}
                onChange={handleChange} placeholder="Enter Organization Name"
                error={errors.organizationName}
              />
              <InputField
                label="Registration Number" required
                name="registrationNumber" value={form.registrationNumber}
                onChange={handleChange} placeholder="Enter Registration Number"
                error={errors.registrationNumber}
              />
              <InputField
                label="First Name" required
                name="firstName" value={form.firstName}
                onChange={handleChange} placeholder="Enter First Name"
                error={errors.firstName}
              />
              <InputField
                label="Last Name" required
                name="lastName" value={form.lastName}
                onChange={handleChange} placeholder="Enter Last Name"
                error={errors.lastName}
              />
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-7">
              {/* ✅ emailAddress readOnly — token இல் இருந்து auto-fill */}
              <InputField
                label="Email Address" required type="email"
                name="emailAddress" value={form.emailAddress}
                onChange={handleChange} placeholder="Enter Email Address"
                error={errors.emailAddress}
                readOnly
              />
              <PhoneField
                label="Phone Number" required
                value={phoneNumber}
                onChange={(val) => {
                  setPhoneNumber(val);
                  if (errors.phoneNumber) setErrors((prev) => ({ ...prev, phoneNumber: "" }));
                }}
                error={errors.phoneNumber}
              />
              <PhoneField
                label="Alternate Phone Number"
                value={alternatePhone}
                onChange={setAlternatePhone}
              />
              <InputField
                label="Street Address" required
                name="streetAddress" value={form.streetAddress}
                onChange={handleChange} placeholder="Enter Street Address"
                error={errors.streetAddress}
              />
            </div>

            {/* Row 3 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-7">

              {/* State — dynamic from API */}
              <div className="flex flex-col gap-1.5">
                <label className="flex items-center gap-0.5 text-sm font-bold text-neutral-800">
                  State/Province <span className="text-red-600 font-bold">*</span>
                </label>
                <div
                  className={`relative h-9 rounded shadow-[0px_0px_4px_0px_rgba(32,33,35,0.07)] border flex items-center bg-white transition-all ${
                    errors.state ? "border-red-400" : "border-gray-200"
                  } focus-within:border-[#4AACC5] focus-within:ring-1 focus-within:ring-[#4AACC5]`}
                >
                  <select
                    name="state"
                    value={form.state}
                    onChange={handleStateSelect}
                    className={`w-full h-full px-3 bg-transparent text-sm appearance-none focus:outline-none cursor-pointer ${
                      form.state ? "text-neutral-800" : "text-zinc-400"
                    }`}
                  >
                    <option value="">Select State</option>
                    {states.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                  <svg className="absolute right-3 w-3 h-3 text-zinc-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {errors.state && <p className="text-xs text-red-500 mt-0.5">{errors.state}</p>}
              </div>

              {/* City — dynamic, depends on state */}
              <div className="flex flex-col gap-1.5">
                <label className="flex items-center gap-0.5 text-sm font-bold text-neutral-800">
                  City <span className="text-red-600 font-bold">*</span>
                </label>
                <div
                  className={`relative h-9 rounded shadow-[0px_0px_4px_0px_rgba(32,33,35,0.07)] border flex items-center transition-all ${
                    !form.state || loadingCities
                      ? "bg-gray-200 border-gray-200"
                      : `bg-white ${errors.city ? "border-red-400" : "border-gray-200"} focus-within:border-[#4AACC5] focus-within:ring-1 focus-within:ring-[#4AACC5]`
                  }`}
                >
                  <select
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    disabled={!form.state || loadingCities}
                    className={`w-full h-full px-3 bg-transparent text-sm appearance-none focus:outline-none ${
                      form.city ? "text-neutral-800" : "text-zinc-400"
                    } ${!form.state || loadingCities ? "cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <option value="">
                      {loadingCities ? "Loading cities..." : "Select City"}
                    </option>
                    {cities.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  <svg className="absolute right-3 w-3 h-3 text-zinc-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {errors.city && <p className="text-xs text-red-500 mt-0.5">{errors.city}</p>}
              </div>

              <InputField
                label="ZIP/Postal Code" required
                name="zipCode" value={form.zipCode}
                onChange={handleChange} placeholder="Enter ZIP/Postal Code"
                error={errors.zipCode}
              />
              <SelectField
                label="Country" required placeholder="Select Country"
                name="country" value={form.country}
                onChange={handleChange} options={COUNTRIES}
                error={errors.country}
              />
            </div>
          </div>
        </SectionCard>

        {/* ── SECTION 2: Accreditation & Verification Documents ── */}
        <SectionCard
          title="Accreditation & Verification Documents"
          subtitle="Upload required documents to complete your accreditation check."
        >
          <div className="flex flex-col lg:flex-row gap-8 items-start">
            <div className="flex-1 flex flex-col gap-6">
              {/* Row 1 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <UploadBox
                  label="Organization Registration Certificate" required
                  name="regCertificate"
                  file={form.regCertificate}
                  uploading={uploadingFiles.regCertificate}
                  onChange={handleFile}
                  error={errors.regCertificate}
                />
                <UploadBox
                  label="Additional Supporting Document"
                  name="additionalDoc"
                  file={form.additionalDoc}
                  uploading={uploadingFiles.additionalDoc}
                  onChange={handleFile}
                />
              </div>

              {/* Extra doc row */}
              {showDocRow && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-1.5">
                    <label className="flex items-center gap-0.5 text-sm font-bold text-neutral-800">
                      Enter Document Type
                      <span className="text-red-600 font-bold">*</span>
                    </label>
                    <input
                      type="text"
                      value={docType}
                      onChange={(e) => setDocType(e.target.value)}
                      placeholder="Enter Document Type"
                      className="h-9 w-full px-3 py-2 bg-white rounded shadow-[0px_0px_4px_0px_rgba(32,33,35,0.07)] border border-gray-200 text-sm text-neutral-800 placeholder-zinc-400 focus:outline-none focus:border-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5] transition-all"
                    />
                  </div>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={handleRemoveDocRow}
                      className="absolute -top-1 -right-1 z-10 w-5 h-5 flex items-center justify-center rounded-full bg-red-100 hover:bg-red-200 text-red-500 transition-colors"
                      title="Remove"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    <div
                      onClick={() => !uploadingFiles.docFile && docFileInputRef.current?.click()}
                      className={`w-full h-24 rounded-[20px] border border-stone-300 flex flex-col items-center justify-center gap-1.5 transition-all group
                        ${uploadingFiles.docFile ? "cursor-wait border-[#4AACC5] bg-teal-50/40" : "cursor-pointer hover:border-[#4AACC5] hover:bg-teal-50/30"}`}
                    >
                      <input
                        ref={docFileInputRef}
                        type="file"
                        className="hidden"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={handleDocFile}
                      />
                      {uploadingFiles.docFile ? (
                        <>
                          <svg className="w-5 h-5 text-[#4AACC5] animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                          <p className="text-xs text-teal-500">Uploading...</p>
                        </>
                      ) : docFile ? (
                        <>
                          <svg className="w-6 h-6 text-[#4AACC5]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-xs text-[#4AACC5] font-medium text-center px-3 truncate max-w-full">{docFile.name}</p>
                        </>
                      ) : (
                        <>
                          <svg className="w-6 h-6 text-zinc-400 group-hover:text-[#4AACC5] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                          </svg>
                          <p className="text-xs text-zinc-400 group-hover:text-teal-500 transition-colors">Click to upload</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!showDocRow && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowDocRow(true)}
                    className="flex items-center gap-2 bg-[#4AACC5] text-white text-sm font-bold px-5 py-2.5 rounded-lg hover:bg-[#4AACC5] transition-all"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    Add Other Documents
                  </button>
                </div>
              )}
            </div>

            {/* ── Org Profile Circle ── */}
            <div className="flex flex-col items-center gap-3 flex-shrink-0">
              <div
                onClick={() => !uploadingFiles.profilePhoto && profileInputRef.current?.click()}
                className={`w-28 h-28 bg-[#4AACC5] rounded-full flex items-center justify-center overflow-hidden transition-all
                  ${uploadingFiles.profilePhoto ? "cursor-wait opacity-70" : "cursor-pointer hover:bg-[#4AACC5]"}`}
              >
                {uploadingFiles.profilePhoto ? (
                  <svg className="w-8 h-8 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                ) : form.profilePhoto ? (
                  <img
                    src={URL.createObjectURL(form.profilePhoto)}
                    alt="Organization Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )}
              </div>
              <input
                ref={profileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleProfilePhoto}
              />
              <button
                type="button"
                onClick={() => profileInputRef.current?.click()}
                disabled={uploadingFiles.profilePhoto}
                className="border border-[#4AACC5] text-[#4AACC5] text-sm font-bold px-4 py-2 rounded-md hover:bg-teal-50 transition-all whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {uploadingFiles.profilePhoto ? "Uploading..." : "Upload Organization Profile"}
              </button>
            </div>
          </div>
        </SectionCard>

        {/* ── SECTION 3: Create User ID & Password ── */}
        <SectionCard
          title="Create User ID & Password"
          subtitle="Create secure account access details"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-7 max-w-4xl">
            {/* ✅ userId readOnly — emailAddress-ஐ mirror பண்ணும் */}
            <InputField
              label="Email" required type="email"
              name="userId" value={form.userId}
              onChange={handleChange} placeholder="Enter Email as User ID"
              error={errors.userId}
            />
            <PasswordField
              label="Password" required
              name="password" value={form.password}
              onChange={handleChange} placeholder="Enter Password"
              error={errors.password}
            />
            <PasswordField
              label="Re-Enter Password" required
              name="rePassword" value={form.rePassword}
              onChange={handleChange} placeholder="Enter Password"
              error={errors.rePassword}
            />
          </div>
        </SectionCard>

        {/* ── BOTTOM ACTIONS ── */}
        <div className="flex items-center justify-between py-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-zinc-600 text-xs font-bold hover:text-[#4AACC5] transition-colors"
          >
            ← Back to Pathway Selection
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || isAnyUploading}
            className="bg-[#4AACC5] text-white text-xs font-bold px-8 py-3 rounded-lg hover:bg-[#96d6e5] transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {(submitting || isAnyUploading) && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            )}
            {submitting
              ? "Submitting..."
              : isAnyUploading
              ? "Uploading Files..."
              : "Submit Application"}
          </button>
        </div>
      </main>

      {/* ── FOOTER ── */}
      <footer className="bg-[#4AACC5] text-white text-center py-4 text-base font-medium">
        © 2026 SkillsMD | All rights reserved
      </footer>
    </div>
  );
};

export default TrainingProviderRegistration;